import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, resolveVoiceById } from "../lib/api";
import { useAudioPreview } from "../lib/useAudioPreview";
import { VoiceFlag, VoiceTags } from "./ui-bits";
import { toast } from "sonner";
import { Search, Loader2, Play, Pause, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 20;

// Acentos reales del catálogo de ai33 por idioma (medidos en vivo contra /v3/voices, no una
// lista inventada) — solo los que tienen volumen suficiente para ser una opción útil; el resto
// de acentos minoritarios (galego, checo, tagalo...) quedan fuera del desplegable pero se
// pueden seguir encontrando por texto libre en la búsqueda.
const ACCENT_OPTIONS = {
  es: ["latin american", "peninsular", "mexican", "colombian", "argentine", "chilean",
       "peruvian", "venezuelan", "andalusian", "dominican", "cuban", "puerto rican"],
  en: ["american", "british", "indian", "australian", "canadian", "nigerian",
       "south african", "irish", "scottish", "new zealand"],
};

// Selector general de voces (ago-2026): el catálogo de ai33 ES el selector, no un "buscar más
// voces" aparte de una lista curada — ai33 es la única vía de síntesis ahora (ver synth_segment
// en el backend, ElevenLabs directo y Edge TTS desconectados), así que no tiene sentido ofrecer
// voces que no vayan a sintetizar. Se usa inline (sin overlay) SOLO en ChannelWizard (paso
// dedicado por completo a elegir voz, nada más compite por el espacio ahí) — en los otros 3
// sitios donde se elige voz (ChannelConfigPage, TabNarracion, OneClickModal) vive dentro de
// VoicePickerButton, que solo muestra la voz ya elegida y abre este catálogo en un popup al
// hacer clic (pedido explícito: esos 3 formularios son más apretados y compiten por espacio).
export default function VoiceSelect({ language, value, onChange }) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [accent, setAccent] = useState("");
  const [gender, setGender] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [voices, setVoices] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentVoice, setCurrentVoice] = useState(null);
  const [currentNotFound, setCurrentNotFound] = useState(false);
  const { playingId, toggle } = useAudioPreview();

  const accentOptions = ACCENT_OPTIONS[language] || ACCENT_OPTIONS.es;

  // La búsqueda libre espera a que el usuario pare de teclear antes de disparar la llamada —
  // sin esto, escribir "mystery" mandaría 8 peticiones seguidas contra el proxy de ai33. Los
  // desplegables (género, categoría, acento) actúan al instante: son elecciones discretas.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const setFilter = (setter) => (v) => { setter(v); setPage(1); };

  const load = useCallback(() => {
    setLoading(true); setError(false);
    const params = new URLSearchParams({ language: language || "es", page: String(page), page_size: String(PAGE_SIZE) });
    if (search) params.set("search", search);
    if (gender) params.set("gender", gender);
    if (category) params.set("category", category);
    if (accent) params.set("accent", accent);
    api.get(`/voices/catalog?${params.toString()}`)
      .then(({ data }) => { setVoices(data.voices || []); setPagination(data.pagination || {}); })
      .catch((e) => { setError(true); toast.error(apiErr(e)); })
      .finally(() => setLoading(false));
  }, [language, page, search, gender, category, accent]);

  useEffect(() => { load(); }, [load]);

  // La voz ya guardada (default_voice de un canal, narration.voice de un proyecto) se fija
  // SIEMPRE arriba del todo, preseleccionada — no solo cuando no aparece en la página/filtro
  // actual (bug real reportado: si por casualidad SÍ caía en la página 1 sin filtros, aparecía
  // enterrada en su posición normal de la lista en vez de la primera, y sin el badge de
  // "actual"). Si ya está cargada en `voices` se reutiliza tal cual (evita una llamada de red
  // redundante); si no, se resuelve aparte — o se avisa claramente si ya no existe en ai33
  // (caso real: voces clonadas que nunca se subieron a ai33, como la de "Guías Secretas").
  useEffect(() => {
    let cancelled = false;
    if (!value) { setCurrentVoice(null); setCurrentNotFound(false); return; }
    const inPage = voices.find((v) => v.id === value);
    if (inPage) { setCurrentVoice(inPage); setCurrentNotFound(false); return; }
    resolveVoiceById(value, language).then((v) => {
      if (cancelled) return;
      setCurrentVoice(v);
      setCurrentNotFound(!v);
    });
    return () => { cancelled = true; };
  }, [value, voices, language]);

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / PAGE_SIZE));

  const VoiceRow = ({ v, pinned = false }) => (
    <button type="button" data-testid={`voice-select-${v.id}`} onClick={() => onChange(v)}
      className={`w-full text-left border-2 rounded-lg p-3 transition-colors ${value === v.id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <VoiceFlag voiceId={v.id} fallbackLang={language || "es"} voiceLang={v.language} />
            <div className="font-bold text-slate-900 truncate">{v.name}</div>
            {pinned && (
              <span className="shrink-0 text-xs font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {t("voiceCat.currentBadge")}
              </span>
            )}
          </div>
          {v.accent && <div className="text-xs text-slate-400 mt-0.5 truncate">{v.accent}</div>}
          <VoiceTags v={v} />
          {v.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {v.preview_url && (
            // onMouseDown con preventDefault (bug real reportado): este control vive dentro del
            // <button> que selecciona la voz — al hacer clic, el navegador movía el foco al
            // <button> padre (comportamiento nativo al clicar un descendiente no enfocable
            // dentro de un elemento enfocable), y con la lista dentro de un popup con scroll
            // anidado (VoicePickerButton), ese cambio de foco arrastraba el scroll hacia arriba.
            // preventDefault en mousedown evita el cambio de foco sin afectar al click.
            <span role="button" data-testid={`voice-select-preview-${v.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { e.stopPropagation(); toggle(v.id, v.preview_url); }}
              className="text-slate-500 hover:text-blue-600 p-1.5 rounded-full hover:bg-blue-50"
              title={t("voiceCat.listen")}>
              {playingId === v.id ? <Pause size={18} /> : <Play size={18} />}
            </span>
          )}
          {value === v.id && <CheckCircle2 size={22} className="text-blue-600" />}
        </div>
      </div>
    </button>
  );

  return (
    <div data-testid="voice-select">
      {currentVoice && (
        <div className="mb-2"><VoiceRow v={currentVoice} pinned /></div>
      )}
      {currentNotFound && (
        <div data-testid="voice-select-current-missing" className="mb-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          {t("voiceCat.currentNotFound")}
        </div>
      )}

      <div className="space-y-3 mb-3">
        <div className="relative">
          <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="voice-select-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("voiceCat.searchPlaceholder")}
            className="w-full border-2 border-slate-200 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:border-blue-500" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <select data-testid="voice-select-gender" value={gender} onChange={(e) => setFilter(setGender)(e.target.value)}
            className="border-2 border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-blue-500">
            <option value="">{t("voiceCat.genderAny")}</option>
            <option value="male">{t("voiceCat.genderMale")}</option>
            <option value="female">{t("voiceCat.genderFemale")}</option>
          </select>
          <select data-testid="voice-select-category" value={category} onChange={(e) => setFilter(setCategory)(e.target.value)}
            className="border-2 border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-blue-500">
            <option value="">{t("voiceCat.categoryAny")}</option>
            <option value="premade">{t("voiceCat.categoryPremade")}</option>
            <option value="professional">{t("voiceCat.categoryProfessional")}</option>
            <option value="high_quality">{t("voiceCat.categoryHighQuality")}</option>
          </select>
          <select data-testid="voice-select-accent" value={accent} onChange={(e) => setFilter(setAccent)(e.target.value)}
            className="border-2 border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-blue-500">
            <option value="">{t("voiceCat.accentAny")}</option>
            {accentOptions.map((a) => <option key={a} value={a}>{t(`voiceCat.accent.${a}`)}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-2 max-h-[360px] overflow-y-auto vb-scroll pr-1" data-testid="voice-select-list">
        {loading ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={26} /></div>
        ) : error ? (
          <div className="py-10 text-center text-red-600 text-sm">{t("voiceCat.loadError")}</div>
        ) : voices.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">{t("voiceCat.noResults")}</div>
        ) : voices.filter((v) => v.id !== value).map((v) => <VoiceRow key={v.id} v={v} />)}
      </div>

      <div className="flex items-center justify-between pt-3 text-sm text-slate-500">
        <span>{t("voiceCat.pageInfo", { page, total: totalPages, count: pagination.total || 0 })}</span>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="voice-select-prev" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="p-2 rounded-lg border-2 border-slate-200 disabled:opacity-40 hover:border-slate-300"><ChevronLeft size={16} /></button>
          <button type="button" data-testid="voice-select-next" disabled={!pagination.has_more || loading} onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-lg border-2 border-slate-200 disabled:opacity-40 hover:border-slate-300"><ChevronRight size={16} /></button>
        </div>
      </div>
    </div>
  );
}
